/**
 * HeatmapTab — sector treemap (S&P 500 style).
 * Hits /api/heatmap to get cells [{symbol, sector, market_cap, change_pct}]
 * and renders as a Plotly treemap with red-green gradient.
 */
import { useQuery } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { api } from '@/lib/api';
import { useStore, readThemeColors } from '@/store';
import { ORG, FONT } from '@/theme';

interface HeatCell {
  symbol:     string;
  sector:     string;
  market_cap: number;
  change_pct: number;
}

export function HeatmapTab() {
  // Subscribe to theme so the colorscale rebuilds on theme swap.
  const theme = useStore(s => s.theme);
  const { gain, loss } = readThemeColors();
  void theme;  // dep for re-render

  const { data, isLoading } = useQuery<HeatCell[]>({
    queryKey: ['heatmap'],
    queryFn:  () => api('/api/heatmap'),
    staleTime: 120_000,
  });

  if (isLoading || !data || data.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
      }}>{isLoading ? 'LOADING HEATMAP...' : 'HEATMAP UNAVAILABLE'}</div>
    );
  }

  // Build treemap parents (sector) + leaves (symbols). A cell can arrive with a
  // null market_cap or change_pct; a bare sum/push there poisons the whole
  // treemap with NaN (Plotly then renders nothing), so coerce non-finite to 0.
  const cap = (d: HeatCell) => (Number.isFinite(d.market_cap) ? d.market_cap : 0);
  const sectors = Array.from(new Set(data.map(d => d.sector)));
  const labels:  string[] = [''];  // root
  const parents: string[] = [''];
  const values:  number[] = [data.reduce((s, d) => s + cap(d), 0)];
  const colors:  number[] = [0];

  for (const s of sectors) { labels.push(s); parents.push(''); values.push(0); colors.push(0); }
  for (const d of data) {
    labels.push(d.symbol);
    parents.push(d.sector);
    values.push(cap(d));
    colors.push(Number.isFinite(d.change_pct) ? d.change_pct : 0);
  }

  return (
    <Plot
      data={[{
        type: 'treemap',
        labels, parents, values,
        branchvalues: 'total',
        marker: {
          colors,
          // Direction colorscale reads from the active theme so CB-Safe
          // and Mono themes show the heatmap in their own palette.
          colorscale: [
            [0,   loss],
            [0.5, '#1a1a1a'],
            [1,   gain],
          ],
          cmin: -5, cmax: 5,
        },
        textinfo: 'label+text',
        text: data.map(d => Number.isFinite(d.change_pct) ? `${d.change_pct >= 0 ? '+' : ''}${d.change_pct.toFixed(2)}%` : '—'),
        hovertemplate: '%{label}<br>%{text}<extra></extra>',
      } as unknown as Plotly.Data]}
      layout={{
        paper_bgcolor: '#090909', plot_bgcolor: '#090909',
        margin: { l: 0, r: 0, t: 0, b: 0 },
        font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 10 },
        treemapcolorway: [ORG],
      } as unknown as Partial<Plotly.Layout>}
      config={{ displayModeBar: false, responsive: true }}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
    />
  );
}
